export interface Site {
    id: string;
    name: string;
}

export interface Device {
    id: string;
    site_id: string;
    name: string;
    interfaces: DeviceInterface[];
    quotas?: Quota[];
}

export interface DeviceInterface {
    alias: string;
    wan_type: string;
}

export interface Quota {
    id?: string;
    site_id: string;
    site_name?: string;
    device_id: string;
    device_name?: string;
    wan_links?: string[];
    access_networks?: string[];
    access_networks_name?: string[];
    access_network_wan_scope?: string[];
    starlink_quota?: StarlinkQuota;
    limit: number;
    period: number;
    period_unit: string;
    period_display?: string;
    start_time: string;
    actions?: Action[];
    current_quota_start_time?: string;
    current_usage?: string;
    current_usage_updated?: string;
    used_percent?: string;
    span?: string;
    estimated_max_over_date?: string | null;
    status?: string;
    notification_methods?: string[],
    users_to_notify?: User[];
    disabled?: boolean;
    quota_type?: string;
    captive_usage_type?: string;
    access_level?: string;
}

export interface StarlinkQuota {
    service_line_numbers: string[];
    enabled: boolean;
}

export interface User {
    name: string;
    email: string;
}
export interface Action {
    id: string;
    usage_percentage: number;
    type: ActionType;
    access_networks?: string[];
    wan_profile_id?: string;
    traffic_policy_id?: string;
    interfaces?: string[];
    device_target?: string;
    device_target_type?: string;
    device_quota_bytes?: number;
    executed: boolean;
    valid?: boolean;
    revert?: boolean;
}

export type ActionType = 'assignWanProfile' | 'assignTrafficPolicy' | 'disableInterfaces' | 'enableInterfaces' | 'sendNotification' | 'predictOverageNotification' | 'resetDevice' | 'assignDevicePolicy' | 'removeDevicePolicy' | '';

export interface AccessNetwork {
    id: string;
    name: string;
    wan_profile: string;
    vlan_aggregate: string;
    captive: boolean;
}

export const Actions = {
    ActionAssignWanProfile: 'assignWanProfile',
    ActionAssignTrafficPolicy: 'assignTrafficPolicy',
    ActionDisableInterface: 'disableInterfaces',
    ActionEnableInterfaces: 'enableInterfaces',
    ActionNotification: 'sendNotification',
    ActionPredictOverageNotification: 'predictOverageNotification',
    ActionResetDevice: 'resetDevice',
    ActionAssignDevicePolicy: 'assignDevicePolicy',
    ActionRemoveDevicePolicy: 'removeDevicePolicy',
    ActionUndefined: ''
} as const;

export interface WanProfile {
    id: string;
    name: string;
}

// GET_TRAFFIC_POLICIES returns both kinds: 'vlan' is a network policy, 'device' a
// device policy. Callers filter with the helpers below.
export const NETWORK_POLICY_TYPE = 'vlan';
export const DEVICE_POLICY_TYPE = 'device';

// An assignment names no client up front: it finds the clients over the per device quota
// and applies the policy to each of their MACs when it fires. A removal can also name one
// of those MACs, to release a single device.
export const DEVICE_TARGET_MAC = 'mac';
export const DEVICE_TARGET_PER_DEVICE_QUOTA = 'per_device_quota';
// the policy goes on the access network itself, where it covers every client on it
export const DEVICE_TARGET_ACCESS_NETWORK = 'access_network';

export interface TrafficPolicy {
    id: string;
    type?: string;
    name: string;
}

export interface StarlinkServiceLine {
    service_line_number?: string;
    nickname?: string;
}

export type QuotaSortBy = 'site_name' | 'device_name' | 'wan_links' | 'current_quota_start_time' | 'period_display' | 'used_percent' | 'status'
export type SortOrder = 'asc' | 'desc'

export const NotificationMethods = {
    'email': 'Email',
    'microsoft_teams': 'Microsoft Teams',
}

export const CAPTIVE_USAGE_AUTHENTICATED = 'authenticated';
export const CAPTIVE_USAGE_ANONYMOUS = 'anonymous';

export const DATA_USAGE_TYPE_LABELS: { [dataUsageType: string]: string } = {
    'interface': 'WAN Interface',
    'access_network': 'Access Network',
    'captive_authenticated': 'Captive Authenticated',
    'captive_anonymous': 'Captive Unauthenticated',
    'starlink_service_line': 'Starlink Service Line',
}

// the captive types count access network usage too, and save which captive users they count
export const CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE: { [dataUsageType: string]: string } = {
    'captive_authenticated': CAPTIVE_USAGE_AUTHENTICATED,
    'captive_anonymous': CAPTIVE_USAGE_ANONYMOUS,
}

export const isCaptiveDataUsageType = (dataUsageType: string): boolean => dataUsageType in CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE;

// Assigning to a device picks clients out of the unauthenticated half of a captive
// network, so it has nobody to act on when the quota counts authenticated usage.
// Assigning to the access network covers everyone on it and applies either way.
export const allowsPerDeviceQuotaActions = (dataUsageType: string): boolean => dataUsageType !== 'captive_authenticated';

export const isAccessNetworkDataUsageType = (dataUsageType: string): boolean => dataUsageType === 'access_network' || isCaptiveDataUsageType(dataUsageType);

export const dataUsageTypeOfQuota = (quota: Quota | null): string => {
    if (quota == null) return '';
    if (quota.wan_links?.length) return 'interface';
    if (quota.access_networks?.length) {
        const captiveType = Object.keys(CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE).find(t => CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE[t] === quota.captive_usage_type);
        return captiveType ?? 'access_network';
    }
    if (quota.starlink_quota?.service_line_numbers?.length) return 'starlink_service_line';
    return '';
}

export const accessNetworkSourceLabel = (captiveUsageType: string | undefined): string => {
    switch (captiveUsageType) {
        case CAPTIVE_USAGE_AUTHENTICATED: return 'Captive Authenticated';
        case CAPTIVE_USAGE_ANONYMOUS: return 'Captive Unauthenticated';
        default: return 'Access Networks';
    }
}

export const WAN_SCOPE_TYPES = [
    'wired',
    'wifi',
    'lte',
    'lband',
    'starlink',
    'starlink_r',
    'oneweb',
    'vsat',
    'vsat_ka',
    'vsat_ku',
    'vsat_highspeed',
    'vsat_unlimited',
    'vsat_voip',
] as const;

export const WAN_TYPE_DISPLAY_LABELS: { [wanType: string]: string } = {
    'wired': 'Ethernet',
    'wifi': 'WIFI',
    'lte': 'Cellular',
    'lband': 'LBand',
    'starlink': 'Starlink',
    'starlink_r': 'Starlink Residential',
    'oneweb': 'OneWeb',
    'vsat': 'VSAT',
    'vsat_ka': 'VSAT Ka-band',
    'vsat_ku': 'VSAT Ku-band',
    'vsat_highspeed': 'VSAT High Speed',
    'vsat_unlimited': 'VSAT Unlimited',
    'vsat_voip': 'VSAT VoIP',
}

export const WAN_TYPE_ALIASES: { [wanType: string]: string[] } = {
    'lte': ['cellular'],
}

export const wanTypeMatches = (wanType: string, deviceWanType: string): boolean =>
    deviceWanType === wanType || (WAN_TYPE_ALIASES[wanType] ?? []).includes(deviceWanType);

export const wanTypeLabel = (wanType: string): string => WAN_TYPE_DISPLAY_LABELS[wanType] ?? wanType;

export const formatWanScope = (scope: string[] | undefined): string => (scope ?? []).map(wanTypeLabel).join(', ');